export default class TagService {
  /**
   * @ngInject *
   * @param {import('lodash').LoDashStatic} _
   * @param {any} $http
   * @param {{ defaultComparisonType: { key: any; }; }} comparisonTypeService
   * @param {{ apiServiceBaseUri: any; }} ngAuthSettings
   * @param {{ getUSStates: () => any; getCanadianProvinces: () => any; }} geoStatesService
   */
  constructor(
    _,
    $http,
    comparisonTypeService,
    ngAuthSettings,
    geoStatesService
  ) {
    this._ = _;
    this.$http = $http;
    this.comparisonTypeService = comparisonTypeService;
    this.ngAuthSettings = ngAuthSettings;
    this.geoStatesService = geoStatesService;

    this.serviceBaseUri = ngAuthSettings.apiServiceBaseUri;
    this.orFilter = {
      tagId: '',
      tagIds: [''],
      comparisonType: comparisonTypeService.defaultComparisonType.key,
      comparisonTypeTemp: comparisonTypeService.defaultComparisonType,
      value: ''
    };
    this.USStatesList = geoStatesService.getUSStates();
    this.canadianProvincesList = geoStatesService.getCanadianProvinces();
    this.memoizedStateNameSearch = _.memoize((
      /** @type {any} */ stateValue
    ) => {
      return _.find(this.USStatesList, (
        /** @type {{ [x: string]: any; }} */ stateObj
      ) => {
        return stateObj['abbreviation'] === stateValue;
      }).name;
    });
    this.memoizedProvinceNameSearch = _.memoize((
      /** @type {any} */ provinceValue
    ) => {
      return _.find(this.canadianProvincesList, (
        /** @type {{ [x: string]: any; }} */ provinceObj
      ) => {
        return provinceObj['abbreviation'] === provinceValue;
      }).name;
    });
    this.inComparisonType = 'IN';

    this.geoTagsIds = [
      {
        tagId: 'Geo:SubDivision',
        stateValueProperty: 'name'
      },
      {
        tagId: 'Geo:SubDivisionCode',
        stateValueProperty: 'abbreviation'
      },
      {
        tagId: 'InboundNumber:State',
        stateValueProperty: 'abbreviation'
      },
      {
        tagId: 'Address:State',
        stateValueProperty: 'abbreviation'
      },
      {
        tagId: 'Lookup:gatherState',
        stateValueProperty: 'abbreviation'
      },
      {
        tagId: 'Lookup:geoState',
        stateValueProperty: 'abbreviation'
      },
      {
        tagId: 'Lookup:icpState',
        stateValueProperty: 'abbreviation'
      },
      {
        tagId: 'Lookup:userState',
        stateValueProperty: 'abbreviation'
      }
    ];

    this.geoProvincesTagsIds = [
      {
        tagId: 'InboundNumber:Province',
        provinceValueProperty: 'name'
      },
      {
        tagId: 'InboundNumber:ProvinceCode',
        provinceValueProperty: 'abbreviation'
      }
    ];
  }

  /**
   * @param {any} accountId
   */
  async getTags(accountId) {
    const url = this.serviceBaseUri + accountId + '/tags/full';
    return this.$http.get(url);
  }

  /**
   * @param {any} accountId
   */
  async getNumberTags(accountId) {
    const { data } = await this.getTags(accountId);
    return data.filter(
      tag => tag && tag.tagType && tag.tagType == 'DialedNumber'
    );
  }

  /**
   * @param {any} accountId
   * @param {string} campaignId
   */
  async getTagsByCampaign(accountId, campaignId) {
    const url =
      this.serviceBaseUri + accountId + '/campaigns/' + campaignId + '/tags';
    return this.$http.get(url);
  }

  /**
   * @param {any} tagCriteria
   */
  getGeoTags(tagCriteria) {
    return this._.filter(tagCriteria, (
      /** @type {{ tagId: any; tagIds: any; }} */ tag
    ) => {
      return this.isGeoTag(tag.tagId, tag.tagIds);
    });
  }

  /**
   * @param {any} tagCriteria
   */
  getProvinceGeoTags(tagCriteria) {
    return this._.filter(tagCriteria, (
      /** @type {{ tagId: any; tagIds: any; }} */ tag
    ) => {
      return this.isProvinceGeoTag(tag.tagId, tag.tagIds);
    });
  }

  /**
   * @param {any} tagId
   * @param {undefined} [tagIds]
   */
  isGeoTag(tagId, tagIds) {
    return (
      this.isStateGeoTag(tagId, tagIds) || this.isProvinceGeoTag(tagId, tagIds)
    );
  }

  /**
   * @param {any} tagId
   * @param {undefined} [tagIds]
   */
  isStateGeoTag(tagId, tagIds) {
    let tag;
    if (tagId) {
      tag = this._findStateGeoTag(tagId);
    } else {
      if (tagIds) {
        tag = this._.find(tagIds, (/** @type {any} */ tag) => {
          return this._findStateGeoTag(tag);
        });
      }
    }
    return !!tag;
  }

  /**
   * @param {any} tagId
   * @param {undefined} [tagIds]
   */
  isProvinceGeoTag(tagId, tagIds) {
    let tag;
    if (tagId) {
      tag = this._findProvinceGeoTag(tagId);
    } else {
      if (tagIds) {
        tag = this._.find(tagIds, (/** @type {any} */ tag) => {
          return this._findProvinceGeoTag(tag);
        });
      }
    }
    return !!tag;
  }

  /**
   * @param {{ comparisonTypeTemp: { value: any; key: any; }; oldComparisonTypeValue: any; }} criteria
   */
  isOldProvinceTag(criteria) {
    // Check if the comparison type is a valid geo criteria
    // If it's not this was created before the new province tag model
    this.geoCriteria = this._.find(this.comparisonTypeService.comparisonTypes, (
      /** @type {{ forGeoTags: any; value: any; }} */ tag
    ) => {
      return (
        tag.forGeoTags &&
        (tag.value == criteria.comparisonTypeTemp.value ||
          tag.value == criteria.comparisonTypeTemp.key ||
          tag.value == criteria.comparisonTypeTemp)
      );
    });
    return !this.geoCriteria && criteria.oldComparisonTypeValue == undefined;
  }

  /**
   * @param {any} tagId
   * @param {undefined} [tagIds]
   */
  allowBulkTagsUpload(tagId, tagIds) {
    if (tagId) {
      return this._allowBulkTagsUpload(tagId);
    }

    if (tagIds) {
      const tag = this._.find(tagIds, (/** @type {any} */ tagId) => {
        if (!this._allowBulkTagsUpload(tagId)) {
          return true;
        }
      });

      return this._allowBulkTagsUpload(tag);
    }
  }

  /**
   * @param {any} tagId
   * @param {any} tagIds
   * @param {any} geoValue
   */
  getGeoValue(tagId, tagIds, geoValue) {
    if (this.isStateGeoTag(tagId, tagIds)) {
      return this.getStateValue(tagId, tagIds, geoValue);
    } else {
      return this.getProvinceValue(tagId, tagIds, geoValue);
    }
  }

  /**
   * @param {any} tagId
   * @param {any} tagIds
   * @param {any} stateValue
   */
  getStateValue(tagId, tagIds, stateValue) {
    if (this.getStateValueProperty(tagId, tagIds) === 'abbreviation') {
      return this.memoizedStateNameSearch(stateValue);
    }
    return stateValue;
  }

  /**
   * @param {any} tagId
   * @param {any} tagIds
   * @param {any} provinceValue
   */
  getProvinceValue(tagId, tagIds, provinceValue) {
    if (this.getProvinceValueProperty(tagId, tagIds) === 'abbreviation') {
      return this.memoizedProvinceNameSearch(provinceValue);
    }
    return provinceValue;
  }

  /**
   * @param {any} tagId
   * @param {any} tagIds
   */
  getStateValueProperty(tagId, tagIds) {
    if (tagId) {
      return this._getStateValueProperty(tagId);
    }

    if (tagIds) {
      const geoTag = this._.find(tagIds, (/** @type {any} */ tag) => {
        return this.isStateGeoTag(tag);
      });

      if (geoTag) {
        return this._getStateValueProperty(geoTag);
      }
    }
  }

  /**
   * @param {any} tagId
   * @param {any} tagIds
   */
  getProvinceValueProperty(tagId, tagIds) {
    if (tagId) {
      return this._getProvinceValueProperty(tagId);
    }

    if (tagIds) {
      const geoTag = this._.find(tagIds, (/** @type {any} */ tag) => {
        return this.isProvinceGeoTag(tag);
      });

      if (geoTag) {
        return this._getProvinceValueProperty(geoTag);
      }
    }
  }

  /**
   * @param {string | any[]} tags
   */
  getTagSources(tags) {
    if (tags && tags.length) {
      return this._.sortBy(
        this._.uniqBy(
          this._.map(tags, (/** @type {{ tagSource: string; }} */ tag) => {
            return {
              id: tag.tagSource.toLowerCase(),
              sourceName: tag.tagSource
            };
          }),
          'id'
        ),
        'id'
      );
    }
    return [];
  }

  /**
   * @param {any} tagCriteria
   */
  groupByGeoTags(tagCriteria) {
    // We group the ORs criteira by the tagId, the criteria and isNegativeMatch
    const groupedTags = this._.groupBy(tagCriteria, (
      /** @type {{ tagId: any; tagIds: any; isNegativeMatch: string; comparisonType: string; }} */ tag
    ) => {
      const tagId = tag.tagId || this._.first(tag.tagIds);

      return tagId + '-' + tag.isNegativeMatch + '-' + tag.comparisonType;
    });

    this._.forEach(groupedTags, (
      /** @type {any} */ groupedObject,
      /** @type {string | number} */ tagId
    ) => {
      groupedTags[tagId] = this._.uniq(this._.map(groupedObject, 'value'));
    });

    return groupedTags;
  }

  /**
   * @param {any} criterias
   */
  normalizeGeoAndBulkTagsInCriterias(criterias) {
    if (!criterias) {
      return;
    }
    const modifiedCriterias = this._.cloneDeep(criterias);
    this._.forEach(modifiedCriterias, (
      /** @type {{ tagCriteria: any; }} */ andObj
    ) => {
      if (this.getGeoTags(andObj.tagCriteria).length) {
        andObj.tagCriteria = this._expandGeoTags(andObj.tagCriteria);
      }

      this._modifyBulkTagInAndObj(andObj);
    });
    return modifiedCriterias;
  }

  /**
   * @param {{ comparisonType?: any; tagId: any; tagIds: any; value?: any; isNegativeMatch: any; comparisonTypeTemp?: any; }} tag
   * @param {boolean} [isBulkTag]
   * @param {boolean} [hasMultipleValues]
   */
  getDefaultTag(tag, isBulkTag, hasMultipleValues) {
    const tagResult = this._.cloneDeep(this.orFilter);
    if (tag) {
      tagResult.tagId = tag.tagId;
      tagResult.tagIds = tag.tagIds;
      tagResult.value = this._joinGeoTag(tag);
      tagResult.valueList = this._splitGeoTag(tag);
      tagResult.comparisonType = tag.comparisonType || tagResult.comparisonType;
      tagResult.isNegativeMatch = !!tag.isNegativeMatch;

      if (isBulkTag) {
        this.comparisonType = hasMultipleValues
          ? this.comparisonTypeService.getMultipleBulkTagComparison()
          : this.comparisonTypeService.getDefaultBulkTagComparison();
        tagResult.comparisonTypeTemp =
          tag.comparisonTypeTemp || this.comparisonType;
      }

      if (tagResult.tagIds == null || tagResult.tagIds.length == 0) {
        delete tagResult.tagIds;
      }
    }

    return tagResult;
  }

  /**
   * @param {any} criteria
   */
  groupGeoTags(criteria) {
    // Geo Tags with Equal criteria are saved as a bunch of "ORs" and Does Not Equal splited by commas
    // Here we group or split them by tagId, criteria and negative values to build a list to be used in the multi-select
    this._.forEach(criteria, (
      /** @type {{ tagCriteria: any[]; }} */ andObj
    ) => {
      const geoTags = this.getGeoTags(andObj.tagCriteria);
      if (geoTags.length) {
        const geoGroupedTags = this.groupByGeoTags(geoTags);
        this._.forEach(geoGroupedTags, (
          /** @type {any} */ states,
          /** @type {string} */ tagId
        ) => {
          const splitKey = tagId.split('-');
          const isNegativeMatch = splitKey[1] === 'true';
          if (!this._isValidGeoComparisonType(splitKey[2], isNegativeMatch)) {
            return;
          }

          const firstIndex = this._.findIndex(andObj.tagCriteria, (
            /** @type {{ tagId: any; tagIds: string | any[]; isNegativeMatch: boolean; comparisonType: any; }} */ t
          ) => {
            return (
              (splitKey[0] === t.tagId ||
                (t.tagIds && t.tagIds.indexOf(splitKey[0]) > -1)) &&
              t.isNegativeMatch == (splitKey[1] === 'true') &&
              t.comparisonType == splitKey[2]
            );
          });

          this._.remove(andObj.tagCriteria, (
            /** @type {{ tagId: any; tagIds: string | any[]; isNegativeMatch: boolean; comparisonType: any; }} */ t
          ) => {
            return (
              (splitKey[0] === t.tagId ||
                (t.tagIds && t.tagIds.indexOf(splitKey[0]) > -1)) &&
              t.isNegativeMatch == (splitKey[1] === 'true') &&
              t.comparisonType == splitKey[2]
            );
          });

          andObj.tagCriteria.splice(
            firstIndex,
            0,
            this.getDefaultTag({
              comparisonType: splitKey[2],
              tagId: splitKey[0],
              tagIds: splitKey[3] ? splitKey[3].split('|') : [],
              value: states,
              isNegativeMatch: isNegativeMatch
            })
          );
        });
      }
    });
  }

  /**
   * @param {any} criteria
   */
  normalizeBulkTagForUI(criteria) {
    this._.forEach(criteria, (
      /** @type {{ tagCriteria: any[]; bulkCriteria: { tagId: any; tagIds: any; isNegativeMatch: any; value: any; id: any; Id: any; }; }} */ andObj
    ) => {
      if (andObj.tagCriteria && andObj.tagCriteria.length) {
        this._.forEach(andObj.tagCriteria, (
          /** @type {{ tagId: any; isBulkTag: boolean; }} */ orObj
        ) => {
          if (this.allowBulkTagsUpload(orObj.tagId)) {
            orObj.isBulkTag = true;
          }
        });
      }

      if (andObj.bulkCriteria) {
        andObj.tagCriteria = andObj.tagCriteria || [];

        const tag = this.getDefaultTag(
          {
            tagId: andObj.bulkCriteria.tagId,
            tagIds: andObj.bulkCriteria.tagIds,
            isNegativeMatch: andObj.bulkCriteria.isNegativeMatch,
            value: andObj.bulkCriteria.value
          },
          true,
          true
        );
        tag.Id = andObj.bulkCriteria.id || andObj.bulkCriteria.Id;
        tag.isBulkTag = true;
        tag.hasMultipleValues = true;

        andObj.tagCriteria.push(tag);

        delete andObj.bulkCriteria;
      }
    });
  }

  /**
   * @param {{ tagCriteria: string | any[]; }} andObj
   * @param {number} andIndex
   */
  normalizeGeoTagsInAndObj(andObj, andIndex) {
    // We transform the geotags with Equal criteria into individual ORs
    // Or join them with comma separator for Does Not Equal
    // The value holds what we save, the valueList holds the joined/split list
    const currentAndObj = andObj.tagCriteria[andIndex];
    if (
      andObj.tagCriteria.length > andIndex &&
      Array.isArray(currentAndObj.valueList)
    ) {
      if (currentAndObj.comparisonType == this.inComparisonType) {
        andObj.tagCriteria[andIndex].value = currentAndObj.valueList.join(',');
      } else {
        andObj.tagCriteria[andIndex].value = currentAndObj.valueList;
      }
    }

    const modifyAndObj = this._.cloneDeep(andObj);
    delete modifyAndObj.bulkCriteria;

    if (this.getGeoTags(modifyAndObj.tagCriteria).length) {
      modifyAndObj.tagCriteria = this._expandGeoTags(modifyAndObj.tagCriteria);
    }

    this._modifyBulkTagInAndObj(modifyAndObj);

    return modifyAndObj;
  }

  /**
   * @param {any[]} tags
   * @param {any} tagSource
   */
  filterByTagSource(tags, tagSource) {
    if (tagSource) {
      return tags.filter((/** @type {{ tagSource: string; }} */ tag) => {
        return tag.tagSource.toLowerCase() === tagSource;
      });
    }
    return tags;
  }

  /**
   * @param {any} tags
   * @param {any} tag
   */
  isTagInArray(tags, tag) {
    return (
      this._.findIndex(tags, (/** @type {any} */ t) => {
        return this.areEqualTags(tag, t);
      }) > -1
    );
  }

  /**
   * @param {any[]} tags
   * @param {any} tag
   */
  pushDedupTag(tags, tag) {
    if (!this.isTagInArray(tags, tag)) {
      tags.push(tag);
    }
  }

  /**
   * @param {{ tagType: any; tagName: any; tagSource: any; }} tag1
   * @param {{ tagType: any; tagName: any; tagSource: any; }} tag2
   */
  areEqualTags(tag1, tag2) {
    return (
      tag1 &&
      tag2 &&
      tag1.tagType == tag2.tagType &&
      tag1.tagName == tag2.tagName &&
      tag1.tagSource == tag2.tagSource
    );
  }

  /**
   * @param {{ value: any[]; }} tag
   */
  _joinGeoTag(tag) {
    if (Array.isArray(tag.value)) {
      return tag.value.join(',');
    }
    return tag.value;
  }

  /**
   * @param {any} tagType
   * @param {boolean} isNegativeMatch
   */
  _isValidGeoComparisonType(tagType, isNegativeMatch) {
    const validComparisonType = this._.filter(
      this.comparisonTypeService.comparisonTypes,
      (
        /** @type {{ forGeoTags: any; key: any; isNegativeMatch: any; }} */ comparisonType
      ) => {
        return (
          comparisonType.forGeoTags &&
          comparisonType.key == tagType &&
          comparisonType.isNegativeMatch == isNegativeMatch
        );
      }
    );

    if (!validComparisonType || validComparisonType.length == 0) {
      return false;
    }
    return true;
  }

  /**
   * @param {{ value: string | string[]; }} tag
   */
  _splitGeoTag(tag) {
    if (Array.isArray(tag.value)) {
      if (tag.value.length > 0 && tag.value[0].split(',').length > 1) {
        return tag.value[0].split(',');
      }
      return tag.value;
    } else if (tag.value) {
      return tag.value.split(',');
    }
  }

  /**
   * @param {any} tagId
   */
  _getStateValueProperty(tagId) {
    return this._findStateGeoTag(tagId).stateValueProperty;
  }

  /**
   * @param {any} tagId
   */
  _getProvinceValueProperty(tagId) {
    return this._findProvinceGeoTag(tagId).provinceValueProperty;
  }

  /**
   * @param {any} tagId
   */
  _allowBulkTagsUpload(tagId) {
    //Always allow bulk tags for all state tags
    return !this.isGeoTag(tagId);
  }

  /**
   * @param {string | any[]} tagIds
   */
  doesHaveMultipleValues(tagIds) {
    //Always allow bulk tags for all state tags
    return tagIds && tagIds.length > 1;
  }

  /**
   * @param {any} tagId
   */
  _findStateGeoTag(tagId) {
    return this._.find(this.geoTagsIds, { tagId: tagId });
  }

  /**
   * @param {any} tagId
   */
  _findProvinceGeoTag(tagId) {
    return this._.find(this.geoProvincesTagsIds, { tagId: tagId });
  }

  /**
   * @param {any[]} tags
   * @param {any} tagNames
   */
  filterByTagNames(tags, tagNames) {
    if (tagNames) {
      return tags.filter((/** @type {{ tagName: string; }} */ tag) => {
        return (
          this._.findIndex(tagNames, (/** @type {string} */ t) => {
            return t.toLowerCase() === tag.tagName.toLowerCase();
          }) != -1
        );
      });
    }
    return tags;
  }

  /**
   * @param {{ tagCriteria: { [x: string]: any; }; bulkCriteria: any; }} modifyAndObj
   */
  _modifyBulkTagInAndObj(modifyAndObj) {
    const bulkTagIndex = this._.findIndex(modifyAndObj.tagCriteria, {
      isBulkTag: true,
      hasMultipleValues: true
    });

    if (bulkTagIndex > -1) {
      modifyAndObj.bulkCriteria = this._.cloneDeep(
        modifyAndObj.tagCriteria[bulkTagIndex]
      );
      this._.remove(modifyAndObj.tagCriteria, {
        isBulkTag: true,
        hasMultipleValues: true
      });
    }
  }

  /**
   * @param {{ [x: string]: any; }} tagCriteria
   * @param {undefined} [searchFrom]
   */
  _expandGeoTags(tagCriteria, searchFrom) {
    const findIndex = this._.findIndex(
      tagCriteria,
      (/** @type {{ tagId: any; tagIds: any; }} */ tag) => {
        return (
          this.isGeoTag(tag.tagId, tag.tagIds) && !this.isOldProvinceTag(tag)
        );
      },
      searchFrom || 0
    );
    if (findIndex > -1) {
      const tagCriteriaCopy = this._.cloneDeep(tagCriteria);
      const expandedTags = this._getGeoCriterias(tagCriteria[findIndex]);
      tagCriteriaCopy.splice.apply(
        tagCriteriaCopy,
        [findIndex, 1].concat(expandedTags)
      );
      return this._expandGeoTags(
        tagCriteriaCopy,
        findIndex + expandedTags.length
      );
    } else {
      return tagCriteria;
    }
  }

  /**
   * @param {{ valueList: any; comparisonType: string; tagId: any; tagIds: any; isNegativeMatch: any; }} tag
   */
  _getGeoCriterias(tag) {
    const tags = [];
    if (tag.valueList && tag.comparisonType == 'IN') {
      tags.push(
        this.getDefaultTag({
          tagId: tag.tagId,
          tagIds: tag.tagIds,
          value: tag.valueList,
          comparisonType: tag.comparisonType,
          isNegativeMatch: tag.isNegativeMatch
        })
      );
    } else {
      this._.forEach(tag.valueList, (/** @type {any} */ state) => {
        tags.push(
          this.getDefaultTag({
            tagId: tag.tagId,
            tagIds: tag.tagIds,
            value: state,
            comparisonType: tag.comparisonType,
            isNegativeMatch: tag.isNegativeMatch
          })
        );
      });
    }
    return tags;
  }

  _getTagsWithCategory(tags) {
    if (Array.isArray(tags) && tags.length > 0) {
      const sortedTags = _.sortBy(tags, ['tagType']);
      let tagType = sortedTags[sortedTags.length - 1].tagType;
      for (let i = sortedTags.length - 1; i >= 0; i--) {
        if (sortedTags[i].tagType != tagType) {
          sortedTags.splice(i + 1, 0, {
            tagType: tagType
          });
          tagType = sortedTags[i].tagType;
        }
        if (i == 0) {
          sortedTags.splice(0, 0, {
            tagType: tagType
          });
        }
        return sortedTags;
      }
    }
    return [];
  }
}
